/** Keep in sync with backend FRONTEND_TOKEN_EXPIRY_SECONDS (default 2592000). */
export const FRONTEND_JWT_TTL_SECONDS = Number(import.meta.env.VITE_FRONTEND_TOKEN_EXPIRY_SECONDS || 2592000);

function base64UrlEncodeBytes(bytes: Uint8Array): string {
  let binary = "";
  for (const b of bytes) binary += String.fromCharCode(b);
  return btoa(binary).replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/g, "");
}

function base64UrlEncodeJson(value: unknown): string {
  const text = JSON.stringify(value);
  return base64UrlEncodeBytes(new TextEncoder().encode(text));
}

async function signHs256(signingInput: string, secret: string): Promise<string> {
  const key = await crypto.subtle.importKey(
    "raw",
    new TextEncoder().encode(secret),
    { name: "HMAC", hash: "SHA-256" },
    false,
    ["sign"],
  );
  const sig = await crypto.subtle.sign(
    "HMAC",
    key,
    new TextEncoder().encode(signingInput),
  );
  return base64UrlEncodeBytes(new Uint8Array(sig));
}

export async function generateFrontendJwtPerRequest(): Promise<string | null> {
  const apiKey = String(import.meta.env.VITE_FRONTEND_API_KEY || "").trim();
  const secretKey = String(import.meta.env.VITE_FRONTEND_SECRET_KEY || "").trim();
  if (!apiKey || !secretKey) {
    if (import.meta.env.DEV) {
      console.warn(
        "[jwtClient] VITE_FRONTEND_API_KEY and VITE_FRONTEND_SECRET_KEY are required for backend API calls."
      );
    }
    return null;
  }
  if (!crypto?.subtle) {
    return null;
  }

  const now = Math.floor(Date.now() / 1000);
  const header = { alg: "HS256", typ: "JWT" };
  const payload = {
    apiKey,
    iat: now - 5,
    exp: now + FRONTEND_JWT_TTL_SECONDS,
  };
  const headerPart = base64UrlEncodeJson(header);
  const payloadPart = base64UrlEncodeJson(payload);
  const signingInput = `${headerPart}.${payloadPart}`;
  const signature = await signHs256(signingInput, secretKey);
  return `${signingInput}.${signature}`;
}
